<script setup lang="ts">
	import type { ClassNameValue } from 'tailwind-merge'

	defineOptions({
		// Enable component caching
		cacheKey: (props: IconLibProps) => `${props.dir || ''}-${props.icon}-${props.width}-${props.height}`,
		cached: true
	})

	export type IconLibProps = {
		icon: string
		dir?: string
		stroke?: string
		rectStroke?: string
		circleStroke?: string
		fill?: string
		height?: number | string
		width?: number | string
		pathFill?: string
		rectFill?: string
		pathClass?: string | string[] | Record<string, boolean>
		rectClass?: string | string[] | Record<string, boolean>
		svgClass?: ClassNameValue
		circleFill?: string
		circleClass?: string | string[] | Record<string, boolean>
	}

	const props = defineProps<IconLibProps>()

	// Glob all SVG files as raw text, eagerly at build time
	const icons = import.meta.glob('~/assets/svg/icons/**/*.svg', { eager: true, query: '?raw&defer' }) as Record<
		string,
		{ default: string }
	>

	// 2️⃣ Build the lookup key whenever `dir` or `icon` changes
	const key = computed(() => {
		const base = '/assets/svg/icons/'
		const dirSeg = props.dir ? `${props.dir}/` : ''
		return `${base}${dirSeg}${props.icon}.svg`
	})

	// Cache for processed SVGs
	const svgCache = new Map<string, string>()

	// Memoized SVG string
	const svgString = computed(() => {
		const cacheKey = JSON.stringify({
			key: key.value,
			props: {
				stroke: props.stroke,
				pathFill: props.pathFill,
				rectStroke: props.rectStroke,
				rectFill: props.rectFill,
				circleStroke: props.circleStroke,
				circleFill: props.circleFill,
				width: props.width,
				height: props.height,
				svgClass: props.svgClass,
				pathClass: props.pathClass,
				rectClass: props.rectClass,
				circleClass: props.circleClass
			}
		})

		if (svgCache.has(cacheKey)) {
			return svgCache.get(cacheKey)
		}

		const result = buildSvg()
		svgCache.set(cacheKey, result)
		return result
	})

	function applyAttrs(
		els: HTMLCollectionOf<SVGPathElement | SVGRectElement | SVGCircleElement>,
		opts: { cls?: string | string[] | Record<string, boolean>; stroke?: string; fill?: string }
	) {
		for (let i = 0; i < els.length; i++) {
			const el = els[i]
			if (opts.cls) el.setAttribute('class', opts.cls.toString())
			if (opts.stroke) {
				el.setAttribute('stroke', opts.stroke)
				el.setAttribute('fill', 'none')
			}
			if (opts.fill) {
				el.setAttribute('fill', opts.fill)
				el.setAttribute('stroke', 'none')
			}
		}
	}

	// 5️⃣ The core build function
	function buildSvg(): string {
		const src = icons[key.value]?.default
		if (!src) {
			return ''
		}

		// parse the SVG text
		const doc = new DOMParser().parseFromString(src, 'image/svg+xml')
		const svgEl = doc.documentElement

		// apply root‑level props
		if (props.height) svgEl.setAttribute('height', `${props.height}`)
		if (props.width) svgEl.setAttribute('width', `${props.width}`)
		if (props.fill) svgEl.setAttribute('fill', props.fill)
		if (props.svgClass) svgEl.setAttribute('class', props.svgClass.toString())

		applyAttrs(svgEl.getElementsByTagName('path'), {
			cls: props.pathClass as string,
			stroke: props.stroke,
			fill: props.pathFill
		})
		applyAttrs(svgEl.getElementsByTagName('rect'), {
			cls: props.rectClass as string,
			stroke: props.rectStroke,
			fill: props.rectFill
		})
		applyAttrs(svgEl.getElementsByTagName('circle'), {
			cls: props.circleClass as string,
			stroke: props.circleStroke,
			fill: props.circleFill
		})

		// serialize back to string
		return new XMLSerializer().serializeToString(svgEl)
	}

	// Clear cache when component is unmounted to prevent memory leaks
	onUnmounted(() => {
		svgCache.clear()
	})
</script>

<template>
	<span v-memo="[svgString]" v-bind="$attrs" v-html="svgString" />
</template>
